import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, CardHeader, ConfirmDialog, ErrorState, Input, MoneyInput, PageHeader, PageLoader, Select, Switch, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { FormSection, KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { masterEntry, masterOptions } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { money } from '../../../utils/currency';
import { itemService } from '../../materials/services/materialService';
import type { Item } from '../../materials/types/materialTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';

const WORK_SAC = '995461'; // electrical installation services
const EMPTY = { name: '', category: '', unit: 'point', sellingRate: null, gstRate: 18, sacHsn: WORK_SAC, specification: '', isActive: true };

const fromItem = (i: Item) => ({
  name: i.name ?? '', category: i.category ?? '', unit: i.unit || 'point', sellingRate: i.sellingRate ?? null,
  gstRate: i.gstRate ?? 18, sacHsn: i.sacHsn || WORK_SAC, specification: i.specification ?? '', isActive: i.isActive ?? true,
});

/** Add / edit one line of the rate card (point-wise or per-job work). */
export default function WorkRateForm() {
  const { id = '' } = useParams();
  const editing = !!id;
  const [params] = useSearchParams();
  const copyFrom = params.get('copy');
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { form, setForm, set, errors, setErrors } = useForm(EMPTY);
  const { ready } = useMasters();

  const sourceId = id || copyFrom;
  const { data: item, loading, error, reload } = useFetch(() => (sourceId ? itemService.get(sourceId) : Promise.resolve(null)), [sourceId]);
  useCrumb(`/work-rates/${id}`, item?.name);

  // New rate: start in the first work category.
  useEffect(() => {
    if (ready && !sourceId) setForm((f) => (f.category ? f : { ...f, category: masterOptions('workCategory')[0]?.value ?? '' }));
  }, [ready, sourceId, setForm]);
  useEffect(() => {
    if (item) setForm({ ...fromItem(item), ...(copyFrom ? { name: `${item.name} (copy)`, isActive: true } : {}) });
  }, [item, copyFrom, setForm]);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (sourceId && loading && !item) return <PageLoader />;
  if (editing && item && item.type !== 'service') return <ErrorState error={{ message: 'This is a material. Open it from Materials.' }} />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.name.trim()) errs.name = 'Enter the work name';
    if (!(form.sellingRate > 0)) errs.sellingRate = 'Enter the rate';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const common = {
      name: form.name.trim(),
      category: form.category,
      unit: form.unit,
      sellingRate: form.sellingRate,
      gstRate: Number(form.gstRate),
      sacHsn: trimOrUndef(form.sacHsn),
      specification: trimOrUndef(form.specification),
    };
    setSaving(true);
    try {
      if (id) await itemService.update(id, { ...common, isActive: form.isActive });
      else await itemService.create({ ...common, type: 'service' });
      toast.success(editing ? 'Rate updated' : 'Work rate added');
      navigate('/work-rates', { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };
  const remove = async () => {
    if (await run('delete', () => itemService.remove(id), `${item?.name ?? 'Work rate'} deleted`)) navigate('/work-rates', { replace: true });
    setConfirmDelete(false);
  };

  const rate = form.sellingRate ?? 0;
  const tax = Math.round((rate * Number(form.gstRate)) / 100);
  const actions = (
    <>
      {editing && <Button variant="dangerText" onClick={() => setConfirmDelete(true)}>Delete</Button>}
      <Button variant="outline" onClick={() => navigate('/work-rates')} disabled={saving}>Cancel</Button>
      <Button type="submit" form="work-rate-form" leftIcon="check" loading={saving}>{editing ? 'Save rate' : 'Add to rate card'}</Button>
    </>
  );

  return (
    <>
      <PageHeader title={editing ? `Edit ${item?.name}` : 'Add work rate'} subtitle={editing ? item?.itemCode : 'One line of your rate card, e.g. "Light point (concealed)" per point.'} actions={actions} />
      <form id="work-rate-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Work">
                <Input label="Work name" required autoFocus value={form.name} onChange={set('name')} error={errors.name} placeholder="e.g. Light point (concealed wiring)" containerClassName="sm:col-span-2" />
                <Select label="Category" options={masterOptions('workCategory', { keep: form.category })} value={form.category || null} onChange={set('category')} placeholder="Pick a category" hint="Add categories in Master data" />
                <Select label="Charged per" options={masterOptions('unit', { keep: form.unit })} value={form.unit} onChange={set('unit')} searchable={false} />
                <Textarea label="What is included" rows={2} value={form.specification} onChange={set('specification')} maxLength={150} containerClassName="sm:col-span-2" placeholder="e.g. Conduit, wiring up to 8 m and switch fitting. Material extra." hint="Shown under the name in lists" />
              </FormSection>
              <FormSection title="Rate and GST">
                <MoneyInput label={`Rate per ${form.unit}`} required value={form.sellingRate} onChange={set('sellingRate')} error={errors.sellingRate} placeholder="0" hint="Before GST" />
                <Select label="GST rate" options={masterOptions('gstRate', { numeric: true, keep: Number(form.gstRate) })} value={Number(form.gstRate)} onChange={set('gstRate')} searchable={false} />
                <Select
                  label="SAC code"
                  options={masterOptions('hsnSac', { keep: form.sacHsn })}
                  value={form.sacHsn || null}
                  onChange={(v) => {
                    const hit = masterEntry('hsnSac', v);
                    setForm((f) => ({ ...f, sacHsn: v ?? '', ...(hit?.gstRate != null ? { gstRate: hit.gstRate } : {}) }));
                  }}
                  clearable
                  hint="995461 = electrical installation services"
                />
              </FormSection>
              {editing && (
                <Card className="p-5">
                  <Switch label="Active" description="Inactive rates are hidden when making quotations and bills" checked={form.isActive} onChange={set('isActive')} />
                </Card>
              )}
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <Card>
              <CardHeader title="Customer pays" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    [`Rate per ${form.unit}`, money(rate)],
                    [`GST ${form.gstRate}%`, money(tax)],
                    ['With GST', money(rate + tax), { strong: true }],
                    ['10 ' + form.unit + (form.unit.endsWith('s') ? '' : 's'), money((rate + tax) * 10)],
                  ]}
                />
              </div>
            </Card>
          }
        />
      </form>
      <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} onConfirm={remove} loading={busy === 'delete'} danger title="Delete this work rate?" message="It will be hidden from the rate card and pickers. Old quotations and bills keep their copy." confirmLabel="Delete rate" />
    </>
  );
}
